import {
  CREATOR_SUBMISSION_STATE_LABELS,
  CREATOR_SUBMISSION_STATE_NOTES,
  FRAUD_CHECKS,
  SUBMISSION_CHECKS,
  creatorSubmissionState,
  type CreatorSubmissionState,
  type Submission,
} from '@nexus/shared';

/**
 * One colour per status family, and no enum code on a screen.
 *
 * ## The rule
 *
 * Verified and paid are green. In review and pending are amber. Held,
 * rejected and failed are red. Draft and anything unrecognised are neutral.
 * That mapping lives HERE and nowhere else, so a creator who has learned that
 * amber means "waiting on somebody" on the Submissions page has learned it for
 * the Wallet and the Payouts queue too.
 *
 * ## Why a domain argument
 *
 * `APPROVED` is two different facts. An APPROVED **earning** is money that
 * survived every check — green. An APPROVED **payout** has been signed off but
 * not sent, which is the operator's "still waiting" — amber. A single global
 * map would have to pick one and be wrong on the other screen, so the caller
 * names the domain when the word is ambiguous and the default covers the rest.
 *
 * ## Why a label function at all
 *
 * The API sends a written sentence wherever one exists (`statusLabel`,
 * `holdReason`, the check notes) and those are always preferred. This is for
 * the raw enum columns that have no sentence — a campaign's status, a user's
 * role, an email's delivery state — which must still never reach a person as
 * `PENDING_REVIEW`.
 */

export type Tone = 'positive' | 'waiting' | 'critical' | 'neutral';

/** Where the word came from, when the same word means different things. */
export type StatusDomain =
  | 'campaign'
  | 'connection'
  | 'creator'
  | 'earning'
  | 'email'
  | 'invitation'
  | 'payout'
  | 'submission'
  | 'support'
  | 'user';

function normalise(status: string): string {
  return status.trim().toUpperCase().replace(/[\s-]+/g, '_');
}

/* The default map: what a word means when nothing else is said. */
const DEFAULT_TONES: Record<string, Tone> = {
  /* Green — it is done, and it went well. */
  ACTIVE: 'positive',
  APPROVED: 'positive',
  COMPLETED: 'positive',
  CONNECTED: 'positive',
  ELIGIBLE: 'positive',
  JOINED: 'positive',
  PAID: 'positive',
  PASS: 'positive',
  SENT: 'positive',
  SUCCEEDED: 'positive',
  SUCCESS: 'positive',
  USED: 'positive',
  VERIFIED: 'positive',

  /* Amber — somebody or something still has to act. */
  CHALLENGE_ISSUED: 'waiting',
  EXCEPTION: 'waiting',
  FUNDED: 'waiting',
  IN_REVIEW: 'waiting',
  OPEN: 'waiting',
  PARTIAL: 'waiting',
  PAUSED: 'waiting',
  PENDING: 'waiting',
  PENDING_CHECKS: 'waiting',
  PENDING_REVIEW: 'waiting',
  PROCESSING: 'waiting',
  QUEUED: 'waiting',
  REQUESTED: 'waiting',
  SENDING: 'waiting',
  SUBMITTED: 'waiting',
  UNCERTAIN: 'waiting',

  /* Red — it stopped, and somebody has to know. (HELD is red by default
     because the default is the ledger's: a held earning is money a rule
     stopped. A held SUBMISSION is a queue, and says so in `DOMAIN_TONES`.) */
  DISABLED: 'critical',
  FAIL: 'critical',
  FAILED: 'critical',
  FAILURE: 'critical',
  HELD: 'critical',
  REJECTED: 'critical',
  REVERSED: 'critical',
  REVOKED: 'critical',
  SUSPENDED: 'critical',

  /* Neutral — nothing has happened yet, or nothing is claimed. */
  CANCELLED: 'neutral',
  CLOSED: 'neutral',
  DRAFT: 'neutral',
  EXPIRED: 'neutral',
  NONE: 'neutral',
  WITHDRAWN: 'neutral',
};

const DOMAIN_TONES: Partial<Record<StatusDomain, Record<string, Tone>>> = {
  /* Signed off but not sent is still the operator's queue, not a finished job. */
  payout: { APPROVED: 'waiting' },
  /* A finished campaign is not a win or a loss, it is over. */
  campaign: { COMPLETED: 'neutral' },
  /*
   * HELD is the other word that is two facts.
   *
   * A held SUBMISSION is in a queue: the automatic checks could not settle the
   * caption, so a person will look at it, and the creator does not have to do
   * anything. The page already tells them so in amber — "Waiting on us", "An
   * operator will look at it — you do not need to repost" — while the pill
   * beside it was red, so one card carried two different verdicts about the
   * same row.
   *
   * A held EARNING is the opposite: a fraud rule fired on evidence and the
   * money is stopped. That one stays red, which is what the default map gives
   * it.
   */
  submission: { HELD: 'waiting' },
  /*
   * A support ticket has two sides, and the colour is about whose turn it
   * is. OPEN is amber for both (somebody has to act, and on the operator's
   * screen that somebody is them). WAITING_ON_CREATOR is neutral for the
   * operator (their part is done) — the creator's page passes its own label
   * and the same neutral tone, because "waiting on you" is not an alarm, it
   * is a turn. RESOLVED is green; CLOSED is over.
   */
  support: { OPEN: 'waiting', WAITING_ON_CREATOR: 'neutral', RESOLVED: 'positive', CLOSED: 'neutral' },
};

export function statusTone(status: string | null | undefined, domain?: StatusDomain): Tone {
  if (!status) return 'neutral';
  const key = normalise(status);
  const override = domain ? DOMAIN_TONES[domain]?.[key] : undefined;
  return override ?? DEFAULT_TONES[key] ?? 'neutral';
}

/* Words that do not survive being title-cased mechanically. */
const SPECIAL_LABELS: Record<string, string> = {
  CHALLENGE_ISSUED: 'Waiting for the code',
  IN_REVIEW: 'In review',
  PENDING_CHECKS: 'Running the checks',
  PENDING_REVIEW: 'In review',
  VERIFIED: 'Verified',
};

const DOMAIN_LABELS: Partial<Record<StatusDomain, Record<string, string>>> = {
  campaign: {
    ACTIVE: 'Live',
    COMPLETED: 'Finished',
    DRAFT: 'Draft',
    FUNDED: 'Funded, not live',
    PAUSED: 'Paused',
    CANCELLED: 'Cancelled',
  },
  connection: {
    PENDING: 'Not proven yet',
    VERIFIED: 'Connected',
    REJECTED: 'Refused',
    REVOKED: 'Disconnected',
  },
  payout: {
    DRAFT: 'Drafted',
    APPROVED: 'Approved, not sent',
    PROCESSING: 'Being sent',
    REQUESTED: 'Requested',
  },
  user: { ACTIVE: 'Active', DISABLED: 'Disabled' },
  email: { QUEUED: 'Queued', SENDING: 'Sending', SENT: 'Delivered', FAILED: 'Failed' },
  /* The operator's words; the creator's page passes its own ("Waiting on you"). */
  support: {
    OPEN: 'Needs a reply',
    WAITING_ON_CREATOR: 'Waiting on the creator',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed',
  },
};

/**
 * `PENDING_REVIEW` → `In review`. Never shown when the API already wrote a
 * sentence about the same row: use that.
 */
export function statusLabel(status: string | null | undefined, domain?: StatusDomain): string {
  if (!status) return 'Unknown';
  const key = normalise(status);
  const fromDomain = domain ? DOMAIN_LABELS[domain]?.[key] : undefined;
  if (fromDomain) return fromDomain;
  const special = SPECIAL_LABELS[key];
  if (special) return special;
  const words = key.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/* ------------------------------------------------------------------ */
/* Check names                                                         */
/* ------------------------------------------------------------------ */

/**
 * A check's name, in words.
 *
 * `CURVE_SHAPE` is the name of a row in the database and it was reaching the
 * Wallet, the Submissions page and the operator's queue unchanged. The plan's
 * own names for these rules are already written down in `@nexus/shared` —
 * "The shape of the view curve" — so this looks them up and only falls back to
 * mechanically de-underscoring a name nobody has written a label for yet.
 */
export function checkName(name: string | null | undefined): string {
  if (!name) return 'A check';
  const key = normalise(name);
  const fraud = FRAUD_CHECKS.find((c) => c.planName === key || c.checkType === key);
  if (fraud) return fraud.label;
  const submission = SUBMISSION_CHECKS.find(
    (c) => c.planName === key || c.checkType === key,
  );
  if (submission) return submission.label;
  return statusLabel(name);
}


/* ------------------------------------------------------------------ */
/* The five words a creator reads on their own history                 */
/* ------------------------------------------------------------------ */

/**
 * The internal statuses, mapped to the words a creator understands.
 *
 * `PostSubmission.status` is the database's vocabulary and it does not answer
 * the question a creator is actually asking. `PENDING_CHECKS` and `IN_REVIEW`
 * are one fact to them — nobody waiting on a clip distinguishes "the automatic
 * checks are running" from "a person has not looked yet", they ask whether
 * anybody has looked. And the fact they care about most once a clip is
 * approved — has the money actually gone out — is not in that column at all;
 * it is on the `Earning`.
 *
 * So the pill on a card reads one of five words: Pending review, Approved,
 * Paid, Rejected, Held. The rule lives in `@nexus/shared` because the API's
 * `?status=` filter has to mean exactly the same thing as this pill: a filter
 * labelled "Approved" that returned a different set of rows from the ones
 * showing an "Approved" pill would be worse than no filter.
 */
export function submissionState(submission: {
  status: Submission['status'];
  earning?: { status: 'APPROVED' | 'REVERSED' | 'PAID' } | null;
}): CreatorSubmissionState {
  return creatorSubmissionState({
    status: submission.status,
    earningStatus: submission.earning?.status ?? null,
  });
}

export function submissionStateLabel(state: CreatorSubmissionState): string {
  return CREATOR_SUBMISSION_STATE_LABELS[state];
}

export function submissionStateNote(state: CreatorSubmissionState): string {
  return CREATOR_SUBMISSION_STATE_NOTES[state];
}

/**
 * The colour of each of those five words.
 *
 * Pending and Held are amber — somebody still has to act, and in both cases
 * that somebody is us, not the creator. Approved and Paid are green. Rejected
 * is red. Deliberately NOT routed through `statusTone`: those five words are
 * this screen's own vocabulary, and one of them (HELD) means something
 * different here from what `HELD` means on an earning.
 */
const STATE_TONES: Record<CreatorSubmissionState, Tone> = {
  PENDING: 'waiting',
  APPROVED: 'positive',
  PAID: 'positive',
  REJECTED: 'critical',
  HELD: 'waiting',
};

export function submissionStateTone(state: CreatorSubmissionState): Tone {
  return STATE_TONES[state];
}
